/**
 * CreateCaseModal - World-Class Legal Design
 * Professional modal for creating new cases - designed for lawyers
 * Full i18n support for English and Norwegian
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Sparkles, Loader2, Wand2, Check, 
  ChevronDown, Plus, UserPlus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useTranslation } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations
const translations = {
  en: {
    newCase: 'New Case',
    client: 'Client',
    selectClient: 'Select client',
    company: 'Company',
    category: 'Category',
    caseTitle: 'Case Title',
    titlePlaceholder: 'Short descriptive title',
    description: 'Description',
    descriptionPlaceholder: 'Describe the case...',
    aiKeywordsPlaceholder: 'Keywords for AI...',
    generate: 'Generate',
    use: 'Use',
    reject: 'Reject',
    contacts: 'Contact Persons',
    selected: 'selected',
    newContact: 'New contact',
    fromClient: 'From client',
    newContacts: 'New contacts',
    noDetails: 'No details',
    namePlaceholder: 'Name *',
    rolePlaceholder: 'Role',
    emailPlaceholder: 'Email',
    phonePlaceholder: 'Phone',
    add: 'Add',
    cancel: 'Cancel',
    createCase: 'Create Case',
    noClientContacts: 'No contacts on client',
    selectClientFirst: 'Select client first',
    nameRequired: 'Name is required',
    selectClientError: 'Select a client',
    enterTitleError: 'Enter a case title',
    caseCreated: 'Case created',
    couldNotCreate: 'Could not create case',
    couldNotGenerate: 'Could not generate description',
    // Categories
    employmentLaw: 'Employment Law',
    familyInheritance: 'Family & Inheritance',
    contractLaw: 'Contract Law',
    realEstate: 'Real Estate',
    damages: 'Damages',
    transaction: 'Transaction',
    corporateLaw: 'Corporate Law',
    tax: 'Tax & Duties',
    ip: 'IP Law',
    dispute: 'Dispute Resolution',
    publicLaw: 'Public Law',
    other: 'Other'
  },
  no: {
    newCase: 'Ny sak',
    client: 'Klient',
    selectClient: 'Velg klient',
    company: 'Bedrift',
    category: 'Kategori',
    caseTitle: 'Sakstittel',
    titlePlaceholder: 'Kort beskrivende tittel',
    description: 'Beskrivelse',
    descriptionPlaceholder: 'Beskriv saken...',
    aiKeywordsPlaceholder: 'Stikkord for AI...',
    generate: 'Generer',
    use: 'Bruk',
    reject: 'Avvis',
    contacts: 'Kontaktpersoner',
    selected: 'valgt',
    newContact: 'Ny kontakt',
    fromClient: 'Fra klient',
    newContacts: 'Nye kontakter',
    noDetails: 'Ingen detaljer',
    namePlaceholder: 'Navn *',
    rolePlaceholder: 'Rolle',
    emailPlaceholder: 'E-post',
    phonePlaceholder: 'Telefon',
    add: 'Legg til',
    cancel: 'Avbryt',
    createCase: 'Opprett sak',
    noClientContacts: 'Ingen kontakter på klient',
    selectClientFirst: 'Velg klient først',
    nameRequired: 'Navn er påkrevd',
    selectClientError: 'Velg en klient',
    enterTitleError: 'Skriv inn sakstittel',
    caseCreated: 'Sak opprettet',
    couldNotCreate: 'Kunne ikke opprette sak',
    couldNotGenerate: 'Kunne ikke generere beskrivelse',
    // Categories
    employmentLaw: 'Arbeidsrett',
    familyInheritance: 'Familie & Arv',
    contractLaw: 'Kontraktsrett',
    realEstate: 'Fast eiendom',
    damages: 'Erstatningsrett',
    transaction: 'Transaksjon',
    corporateLaw: 'Selskapsrett',
    tax: 'Skatt & Avgift',
    ip: 'Immaterialrett',
    dispute: 'Tvisteløsning',
    publicLaw: 'Offentlig rett',
    other: 'Annet'
  }
};

interface Contact {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  isNew?: boolean;
}

interface CreateCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedClientId?: string;
  onSuccess?: (caseId: string) => void;
}

const CreateCaseModal: React.FC<CreateCaseModalProps> = ({ 
  isOpen, 
  onClose, 
  preSelectedClientId,
  onSuccess 
}) => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const t = translations[language] || translations.en;
  
  // Categories with translated labels
  const CATEGORIES = [
    { value: 'arbeidsrett', label: t.employmentLaw },
    { value: 'familie_arv', label: t.familyInheritance },
    { value: 'kontraktsrett', label: t.contractLaw },
    { value: 'eiendom', label: t.realEstate },
    { value: 'erstatning', label: t.damages },
    { value: 'transaksjon', label: t.transaction },
    { value: 'selskapsrett', label: t.corporateLaw },
    { value: 'skatt', label: t.tax },
    { value: 'ip', label: t.ip },
    { value: 'tvist', label: t.dispute },
    { value: 'offentlig', label: t.publicLaw },
    { value: 'annet', label: t.other }
  ];

  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: preSelectedClientId || '',
    topic: '',
    description: '',
    category: ''
  });
  
  const [clientContacts, setClientContacts] = useState<Contact[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [newContacts, setNewContacts] = useState<Contact[]>([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', role: '', email: '', phone: '' });
  
  const [aiMode, setAiMode] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      if (preSelectedClientId) {
        setFormData(prev => ({ ...prev, client_id: preSelectedClientId }));
      }
    }
  }, [isOpen, preSelectedClientId]);

  useEffect(() => {
    if (formData.client_id) {
      loadClientContacts(formData.client_id);
    } else {
      setClientContacts([]);
      setSelectedContactIds(new Set());
    }
  }, [formData.client_id, clients]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const currentUser = auth.getUser();
      if (!currentUser) return;
      const response = await axios.get(`${API}/crm/clients/${currentUser.id}`);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Could not fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClientContacts = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    if (client?.contacts && Array.isArray(client.contacts)) {
      const contacts = client.contacts.map((c: any, i: number) => ({
        id: c.id || `existing-${i}`,
        name: c.name || '',
        role: c.role || '',
        email: c.email || '',
        phone: c.phone || ''
      }));
      setClientContacts(contacts);
    } else {
      setClientContacts([]);
    }
    setSelectedContactIds(new Set());
    setNewContacts([]);
  };

  const toggleContactSelection = (contactId: string) => {
    const newSelected = new Set(selectedContactIds);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContactIds(newSelected);
  };

  const handleAddNewContact = () => {
    if (!newContact.name?.trim()) {
      toast.error(t.nameRequired);
      return;
    }
    setNewContacts([...newContacts, { ...newContact, id: `new-${Date.now()}`, isNew: true }]);
    setNewContact({ name: '', role: '', email: '', phone: '' });
    setShowAddContact(false);
  };

  const handleRemoveNewContact = (id: string) => {
    setNewContacts(newContacts.filter(c => c.id !== id));
  };

  const handleGenerateWithAI = async () => {
    if (!keywords.trim()) return;
    setAiGenerating(true);
    try {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      const clientName = selectedClient?.company_name || selectedClient?.person_name || selectedClient?.name;
      const response = await axios.post(`${API}/ai/cases/expand-description`, {
        keywords, client_name: clientName, case_type: formData.category
      });
      setAiSuggestion(response.data);
    } catch (error) {
      toast.error(t.couldNotGenerate);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAcceptAI = () => {
    if (aiSuggestion) {
      setFormData({
        ...formData,
        description: aiSuggestion.expanded_description,
        topic: aiSuggestion.suggested_title || formData.topic,
        category: aiSuggestion.suggested_category || formData.category
      });
      setAiSuggestion(null);
      setAiMode(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.topic.trim()) {
      toast.error(!formData.client_id ? t.selectClientError : t.enterTitleError);
      return;
    }
    
    const selectedExisting = clientContacts.filter(c => selectedContactIds.has(c.id));
    const allContacts = [...selectedExisting, ...newContacts];
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/cases`, {
        topic: formData.topic,
        intake_summary: formData.description,
        client_id: formData.client_id,
        category: formData.category,
        contacts: allContacts.filter(c => c.name)
      });
      toast.success(t.caseCreated);
      onSuccess?.(response.data?.id);
      onClose();
      if (response.data?.id) navigate(`/lexy/case/${response.data.id}/workspace`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t.couldNotCreate);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedClient = clients.find(c => c.id === formData.client_id);
  const clientName = selectedClient?.company_name || selectedClient?.person_name || selectedClient?.name;
  const totalSelectedContacts = selectedContactIds.size + newContacts.length;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ type: 'spring', duration: 0.25, bounce: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden pointer-events-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t.newCase}</h2>
                  {clientName && <p className="text-sm text-gray-500">{clientName}</p>}
                </div>
                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                
                {/* Client */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t.client}</label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <select
                        value={formData.client_id}
                        onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                        className="w-full h-10 pl-3 pr-10 text-sm bg-white border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      >
                        <option value="">{t.selectClient}</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.company_name || client.person_name || client.name}
                            {client.type === 'COMPANY' ? ` (${t.company})` : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    <button
                      type="button"
                      onClick={() => { onClose(); navigate('/lexy/clients/new'); }}
                      className="h-10 px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t.category}</label>
                  <div className="grid grid-cols-4 gap-1">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData({...formData, category: cat.value})}
                        className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${
                          formData.category === cat.value
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t.caseTitle}</label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({...formData, topic: e.target.value})}
                    placeholder={t.titlePlaceholder}
                    className="w-full h-10 px-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-gray-700">{t.description}</label>
                    <button
                      type="button"
                      onClick={() => setAiMode(!aiMode)}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                        aiMode ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <Sparkles className="w-3 h-3" />
                      AI
                    </button>
                  </div>

                  {!aiMode ? (
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full h-20 p-3 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder={t.descriptionPlaceholder}
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <textarea
                          value={keywords}
                          onChange={(e) => setKeywords(e.target.value)}
                          className="w-full h-14 p-2 text-sm border border-gray-200 rounded resize-none focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white"
                          placeholder={t.aiKeywordsPlaceholder}
                        />
                        <button
                          type="button"
                          onClick={handleGenerateWithAI}
                          disabled={aiGenerating || !keywords.trim()}
                          className="mt-2 w-full h-8 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                        >
                          {aiGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3" /> {t.generate}</>}
                        </button>
                      </div>
                      {aiSuggestion && (
                        <div className="border border-gray-200 rounded-lg p-3 bg-white">
                          {aiSuggestion.suggested_title && <p className="text-sm font-medium text-gray-900 mb-1">{aiSuggestion.suggested_title}</p>}
                          <p className="text-xs text-gray-600 leading-relaxed max-h-20 overflow-y-auto">{aiSuggestion.expanded_description}</p>
                          <div className="flex gap-2 mt-2">
                            <button onClick={handleAcceptAI} className="flex-1 h-7 bg-gray-900 text-white rounded text-xs font-medium hover:bg-gray-800 flex items-center justify-center gap-1">
                              <Check className="w-3 h-3" /> {t.use}
                            </button>
                            <button onClick={() => setAiSuggestion(null)} className="px-3 h-7 text-gray-500 hover:bg-gray-100 rounded text-xs font-medium">{t.reject}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Contacts */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      {t.contacts}
                      {totalSelectedContacts > 0 && (
                        <span className="ml-1.5 text-xs text-gray-400">({totalSelectedContacts} {t.selected})</span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAddContact(true)}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <UserPlus className="w-3 h-3" />
                      {t.newContact}
                    </button>
                  </div>

                  {/* Existing client contacts */}
                  {clientContacts.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-400 mb-1">{t.fromClient}</p>
                      <div className="space-y-1">
                        {clientContacts.map((contact) => (
                          <label
                            key={contact.id}
                            className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer transition-all ${
                              selectedContactIds.has(contact.id)
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedContactIds.has(contact.id)}
                              onChange={() => toggleContactSelection(contact.id)}
                              className="sr-only"
                            />
                            <div className={`w-7 h-7 rounded flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                              selectedContactIds.has(contact.id) ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {contact.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium truncate ${selectedContactIds.has(contact.id) ? 'text-white' : 'text-gray-900'}`}>
                                {contact.name}
                              </p>
                              <p className={`text-xs truncate ${selectedContactIds.has(contact.id) ? 'text-gray-300' : 'text-gray-500'}`}>
                                {[contact.role, contact.email].filter(Boolean).join(' • ') || t.noDetails}
                              </p>
                            </div>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedContactIds.has(contact.id)
                                ? 'border-white bg-white'
                                : 'border-gray-300'
                            }`}>
                              {selectedContactIds.has(contact.id) && <Check className="w-3 h-3 text-gray-900" />}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New contacts */}
                  {newContacts.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-400 mb-1">{t.newContacts}</p>
                      <div className="space-y-1">
                        {newContacts.map((contact) => (
                          <div key={contact.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg group">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center text-gray-600 text-xs font-medium flex-shrink-0">
                                {contact.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                                <p className="text-xs text-gray-500 truncate">
                                  {[contact.role, contact.email].filter(Boolean).join(' • ')}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveNewContact(contact.id)}
                              className="opacity-0 group-hover:opacity-100 w-6 h-6 text-gray-400 hover:text-red-500 rounded transition-all flex items-center justify-center"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add new contact form */}
                  <AnimatePresence>
                    {showAddContact && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={newContact.name}
                              onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                              placeholder={t.namePlaceholder}
                              className="h-8 px-2 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-900"
                            />
                            <input
                              type="text"
                              value={newContact.role}
                              onChange={(e) => setNewContact({...newContact, role: e.target.value})}
                              placeholder={t.rolePlaceholder}
                              className="h-8 px-2 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-900"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="email"
                              value={newContact.email}
                              onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                              placeholder={t.emailPlaceholder}
                              className="h-8 px-2 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-900"
                            />
                            <input
                              type="tel"
                              value={newContact.phone}
                              onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                              placeholder={t.phonePlaceholder}
                              className="h-8 px-2 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-900"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={handleAddNewContact} className="flex-1 h-7 bg-gray-900 text-white rounded text-xs font-medium hover:bg-gray-800">
                              {t.add}
                            </button>
                            <button onClick={() => setShowAddContact(false)} className="px-3 h-7 text-gray-500 hover:bg-gray-100 rounded text-xs font-medium">
                              {t.cancel}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Empty state */}
                  {clientContacts.length === 0 && newContacts.length === 0 && !showAddContact && (
                    <p className="text-xs text-gray-400 py-2">
                      {formData.client_id ? t.noClientContacts : t.selectClientFirst}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0 bg-white">
                <button onClick={onClose} className="flex-1 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">
                  {t.cancel}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.client_id || !formData.topic.trim()}
                  className="flex-1 h-10 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.createCase}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateCaseModal;
